// AUTODEX 이용도우미 챗봇 위젯
//  - AI 모드: 기능 안내 Q&A (POST /api/assistant/chat)
//  - 상담 모드(2a): "상담원 연결" → 관리자 실시간 상담 (POST /api/support/*)
//    비동기+알림 하이브리드: 소켓 support:reply 신호 + 12초 폴백 폴링으로 답변 반영.
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API, authFetch } from '../utils/api';
import { getSocket } from '../utils/socket';
import * as bus from '../utils/assistantBus';
import { renderMarkdown } from './assistantMarkdown';
import { useAssistantLayout } from './assistantLayout';
import { openBugReport } from './BugReportButton';
import './AssistantWidget.css';

var CHAT_KEY_PREFIX = 'autodex_assistant_chat_';

// 로그인할 때마다 AI 대화는 새로 시작한다(Jin 2026-10-16) — 저장된 대화를 계정 구분 없이 비운다.
export function clearAssistantChats() {
  try {
    for (var i = localStorage.length - 1; i >= 0; i--) {
      var k = localStorage.key(i);
      if (k && k.indexOf(CHAT_KEY_PREFIX) === 0) localStorage.removeItem(k);
    }
  } catch (_e) {}
}

var WELCOME = '안녕하세요! AUTODEX AI도우미입니다. 차량 등록, 말소대행, 인보이스, 세무 등 사용법이 궁금하시면 물어보세요.';
var SUPPORT_INTRO = '관리자 상담으로 연결합니다. 문의 내용을 남겨 주세요. 관리자가 확인하면 답변을 드립니다(부재 시 알림으로 안내).';
var SHOT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
// 접수 버튼 문구 — 화면 신고 폼의 분류 라벨과 같은 말 (BugReportButton.js:185 catKr)
var REPORT_CAT_KR = { bug: '버그', improvement: '개선요청', question: '질문' };
var SHOT_MAX = 10 * 1024 * 1024;
// 추천 질문 — 등록된 가이드가 있으면 그 목록(서버가 역할로 걸러 준다)이 칩이 되고,
// 없을 때 쓰는 기본값이다. **역할마다 다르다** — 말소대행 계정에 "차량 등록증 올리기" 가
// 뜨던 문제(2026-10-13 실측). 역할 키는 서버 assistantKb.roleKey 와 같은 값.
var SUGGESTIONS_BY_ROLE = {
  doc_agency: [
    '위탁 업체는 어떻게 추가하나요?',
    '업체를 바꿔가며 보려면 어떻게 해요?',
    '위탁 업체 세무 마감은 어떻게 해요?',
    '위탁사 인보이스를 대신 작성하려면?'
  ],
  cancel_agency: [
    '대행요청은 어떤 순서로 처리하나요?',
    '말소증은 어떻게 올리나요?',
    '보강서류 요청은 어디서 하나요?',
    '정산은 언제 생성되나요?'
  ]
};
var SUGGESTIONS_DEFAULT = [
  '차량 등록증은 어떻게 올리나요?',
  '말소대행 신청은 어디서 하나요?',
  '인보이스를 관세사에게 보내려면?',
  '부가세 환급은 어떻게 확인하나요?'
];
function suggestionsFor(role) {
  return SUGGESTIONS_BY_ROLE[role] || SUGGESTIONS_DEFAULT;   // user·admin·그 외는 기본값
}

function AssistantWidget(props) {
  var user = props.user;
  // AI 대화 로컬 보관 — 닫기/새로고침/화면이동 후에도 복원 (per-user, 24h TTL)
  var UID = (user && (user.id != null ? user.id : user.userId)) || 'anon';
  var CHAT_KEY = CHAT_KEY_PREFIX + UID;
  var STORE_TTL = 24 * 60 * 60 * 1000;
  var L = useAssistantLayout('autodex_assistant_layout_' + UID);   // 위치·크기 (사용자별)
  var _open = useState(false); var open = _open[0]; var setOpen = _open[1];
  var _mode = useState('ai'); var mode = _mode[0]; var setMode = _mode[1]; // 'ai' | 'support'  (에이전트는 탭이 아니라 서버 자동 분기 — 2026-10-14)
  var STORE_KEY = CHAT_KEY;
  var loadMsgs = function (key) {
    try {
      var raw = localStorage.getItem(key);
      if (raw) {
        var obj = JSON.parse(raw);
        if (obj && obj.ts && Array.isArray(obj.msgs) && obj.msgs.length && (Date.now() - obj.ts) < STORE_TTL) return obj.msgs;
      }
    } catch (_e) {}
    return [{ role: 'assistant', content: WELCOME }];
  };
  var _agentOn = useState(false); var agentEnabled = _agentOn[0]; var setAgentEnabled = _agentOn[1];
  // 노출 여부 — 서버 게이트(ASSISTANT_ENABLED '1'·'beta')가 이 계정에 /assistant/config 를 200 으로 줄 때만 뜬다.
  //   404·네트워크 실패는 숨김(fail-closed). 운영 베타 공개 2026-10-14 — 예전 호스트명 게이트(assistantGate) 대체.
  var _avail = useState(false); var available = _avail[0]; var setAvailable = _avail[1];

  // AI 대화 (마운트 시 localStorage 에서 복원)
  var _msgs = useState(function () { return loadMsgs(CHAT_KEY); });
  var msgs = _msgs[0]; var setMsgs = _msgs[1];
  var _input = useState(''); var input = _input[0]; var setInput = _input[1];
  var _busy = useState(false); var busy = _busy[0]; var setBusy = _busy[1];
  var _shotOn = useState(false); var screenshotEnabled = _shotOn[0]; var setScreenshotEnabled = _shotOn[1];
  var _shot = useState(null); var shot = _shot[0]; var setShot = _shot[1];
  var _shotPrev = useState(''); var shotPrev = _shotPrev[0]; var setShotPrev = _shotPrev[1];
  var _shotNote = useState(''); var shotNote = _shotNote[0]; var setShotNote = _shotNote[1];
  var _guideList = useState([]); var guideList = _guideList[0]; var setGuideList = _guideList[1];
  var _ctxCar = useState(null); var ctxCar = _ctxCar[0]; var setCtxCar = _ctxCar[1];   // { id, no } — 차량 상세에서 물어본 대화의 기준 차량 (메모리만, 복원 안 함)
  var _guideImgUrls = useState({}); var guideImgUrls = _guideImgUrls[0]; var setGuideImgUrls = _guideImgUrls[1];

  // 상담(support)
  var _thread = useState(null); var threadId = _thread[0]; var setThreadId = _thread[1];
  var _smsgs = useState([]); var smsgs = _smsgs[0]; var setSmsgs = _smsgs[1];
  var _sinput = useState(''); var sinput = _sinput[0]; var setSinput = _sinput[1];
  var _ssending = useState(false); var ssending = _ssending[0]; var setSsending = _ssending[1];
  var _sfiles = useState([]); var sfiles = _sfiles[0]; var setSfiles = _sfiles[1];
  var _sprev = useState([]); var spreviews = _sprev[0]; var setSpreviews = _sprev[1];
  var _attUrls = useState({}); var attUrls = _attUrls[0]; var setAttUrls = _attUrls[1];
  var _lightbox = useState(''); var lightbox = _lightbox[0]; var setLightbox = _lightbox[1];
  var _sthreads = useState([]); var sthreads = _sthreads[0]; var setSthreads = _sthreads[1];
  var _sview = useState('list'); var sview = _sview[0]; var setSview = _sview[1]; // 'list' | 'thread'
  var MAX_ATT = 5;

  var scrollRef = useRef(null);
  var threadIdRef = useRef(null);
  var sendRef = useRef(null);
  threadIdRef.current = threadId;

  var currentPage = window.location.hash || window.location.pathname;
  var navigate = useNavigate();      // 위젯은 /main/* Route 의 Dashboard 안 — 같은 Router 트리
  var location = useLocation();

  // 노출·에이전트 허용 여부 — 실패/404 는 위젯 숨김
  useEffect(function () {
    if (!user) return undefined;
    try {
      authFetch(API + '/assistant/config').then(function (r) { return r.status === 200 ? r.json() : null; })
        .then(function (d) { if (!d) return; setAvailable(true); setAgentEnabled(!!d.agentEnabled); setScreenshotEnabled(!!d.screenshotEnabled); bus.setConfig({ available: true, agentEnabled: !!d.agentEnabled }); }).catch(function () {});
    } catch (_e) {}
    return function () { bus.setConfig(null); };
  }, []);
  // 차량 상세 ⋯ "AI에게 이 차량 물어보기" — 에이전트 허용 계정만. 위젯을 열고 그 차량 기준(context.car_id)으로 바로 묻는다.
  useEffect(function () {
    return bus.onAsk(function (a) {
      if (!a || !a.carId || !bus.getConfig().agentEnabled) return;
      setMode('ai'); setOpen(true); setCtxCar({ id: a.carId, no: a.carNo || '' }); clearShot(); setShotNote('');   // 차량 기준 대화(에이전트)는 이미지 첨부 불가 — 서버 400 screenshot_not_in_agent
      if (sendRef.current) sendRef.current(a.question || bus.carQuestion(a.carNo), { carId: a.carId });
    });
  }, []);
  // 모드를 바꾸면(AI ↔ 관리자 상담) 첨부도 비운다 — 상담 화면에는 첨부가 없어서, 남겨 두면 AI 로 돌아올 때
  // 묵은 이미지가 되살아난다.
  var switchMode = function (m) { setMsgs(loadMsgs(CHAT_KEY)); setMode(m); setInput(''); clearShot(); setShotNote(''); };

  useEffect(function () {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, smsgs, open, busy, mode]);

  // AI 대화 로컬 저장 — 인사만 남은 초기 상태(또는 ↻ 초기화)면 삭제해 새 세션으로
  useEffect(function () {
    try {
      if (msgs.length <= 1) localStorage.removeItem(STORE_KEY);
      else localStorage.setItem(STORE_KEY, JSON.stringify({ ts: Date.now(), msgs: msgs }));
    } catch (_e) {}
  }, [msgs]);

  // 상담 답변 실시간 반영: 소켓 신호 + 폴백 폴링
  useEffect(function () {
    if (!open || mode !== 'support' || !threadId) return undefined;
    var alive = true;
    var refetch = function () {
      authFetch(API + '/support/threads/' + threadId + '/messages')
        .then(function (r) { return r.json(); })
        .then(function (d) { if (alive && d && d.messages) setSmsgs(d.messages); })
        .catch(function () {});
    };
    var sock = null;
    var onReply = function (data) { if (data && String(data.threadId) === String(threadIdRef.current)) refetch(); };
    try { sock = getSocket(); if (sock) sock.on('support:reply', onReply); } catch (_e) {}
    var poll = setInterval(refetch, 12000);
    return function () {
      alive = false; clearInterval(poll);
      try { if (sock) sock.off('support:reply', onReply); } catch (_e) {}
    };
  }, [open, mode, threadId]);

  // 첨부 blob 로딩 (인증 필요 → authFetch 후 objectURL 캐시)
  useEffect(function () {
    if (mode !== 'support' || !threadIdRef.current) return undefined;
    smsgs.forEach(function (m) {
      (m.attachments || []).forEach(function (a) {
        if (attUrls[a.id]) return;
        authFetch(API + '/support/threads/' + threadIdRef.current + '/attachments/' + a.id)
          .then(function (r) { return r.blob(); })
          .then(function (blob) {
            var u = URL.createObjectURL(blob);
            setAttUrls(function (prev) { if (prev[a.id]) return prev; var n = Object.assign({}, prev); n[a.id] = u; return n; });
          }).catch(function () {});
      });
    });
    return undefined;
  }, [smsgs, mode]);

  // FAQ 스크린샷 가이드 목록 로드 (위젯 열릴 때)
  useEffect(function () {
    if (!open) return undefined;
    authFetch(API + '/assistant/guides').then(function (r) { return r.json(); })
      .then(function (d) { if (d && d.guides) setGuideList(d.guides); }).catch(function () {});
    return undefined;
  }, [open]);

  // 가이드 이미지 blob 로딩 (인증 필요)
  useEffect(function () {
    msgs.forEach(function (m) {
      if (!m.guide) return;
      (m.guide.images || []).forEach(function (im) {
        if (guideImgUrls[im.id]) return;
        authFetch(API + '/assistant/guides/' + m.guide.guideId + '/images/' + im.id)
          .then(function (r) { return r.blob(); })
          .then(function (b) { var u = URL.createObjectURL(b); setGuideImgUrls(function (p) { if (p[im.id]) return p; var n = Object.assign({}, p); n[im.id] = u; return n; }); })
          .catch(function () {});
      });
    });
  }, [msgs]);

  // ── 스크린샷 첨부 (1장, 베타 계정만) ──
  var clearShot = function () {
    setShotPrev(function (u) { if (u) { try { URL.revokeObjectURL(u); } catch (_e) {} } return ''; });
    setShot(null);
  };
  var attachShot = function (f) {
    if (!f) return;
    if (SHOT_TYPES.indexOf(f.type) < 0) { setShotNote('JPG, PNG, WEBP 형식만 첨부할 수 있습니다.'); return; }
    if (f.size > SHOT_MAX) { setShotNote('10MB 이하 이미지만 첨부할 수 있습니다.'); return; }
    setShotNote('');
    setShotPrev(function (u) { if (u) { try { URL.revokeObjectURL(u); } catch (_e) {} } return URL.createObjectURL(f); });
    setShot(f);
  };
  var onShotSelect = function (e) {
    if (e.target.files && e.target.files[0]) attachShot(e.target.files[0]);
    e.target.value = '';
  };
  var onShotPaste = function (e) {
    if (!screenshotEnabled || ctxCar) return;
    var items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].type).indexOf('image') === 0) { var f = items[i].getAsFile(); if (f) { attachShot(f); return; } }
    }
  };

  // ── AI 전송 ──
  var send = function (text, opts) {
    var q = String(text == null ? input : text).trim();
    if (!q || busy) return;
    setInput('');
    var shotFile = screenshotEnabled && !ctxCar && !(opts && opts.carId) ? shot : null;   // 차량 기준 대화는 이미지 불가(서버 400) — 첫 ask 는 ctxCar state 반영 전이라 opts 도 본다
    var base = msgs.concat([shotFile ? { role: 'user', content: q, shot: true } : { role: 'user', content: q }]);
    setMsgs(base); setBusy(true);
    // 첨부는 **성공한 뒤에만** 비운다 — 먼저 비우면 네트워크 오류·400 때 사용자가 캡처를 다시 떠야 한다.
    var shotKept = shotFile;
    var history = base.filter(function (m) { return m.content !== WELCOME; })
      .map(function (m) { return { role: m.role, content: m.content }; });
    // 에이전트 허용 계정은 auto — 서버의 Haiku 분류기가 설명서(챗봇)/데이터(에이전트) 경로를 정한다.
    var chatPayload = agentEnabled ? { messages: history, page: currentPage, mode: 'auto' } : { messages: history, page: currentPage };
    // 화면 모드(검차 화면=simple / 차량관리 화면=normal) — 답변이 사용자가 보는 화면에 맞도록. 판정 불가(prop 없음)면 키를 보내지 않는다.
    if (typeof props.screenMode === 'boolean') chatPayload.screen_mode = props.screenMode ? 'simple' : 'normal';
    var ctxCarId = (opts && opts.carId) || (ctxCar && ctxCar.id);
    if (ctxCarId) chatPayload.context = { car_id: ctxCarId };
    var chatBody;
    if (shotFile) { chatBody = new FormData(); chatBody.append('payload', JSON.stringify(chatPayload)); chatBody.append('image', shotFile); }
    else chatBody = JSON.stringify(chatPayload);
    authFetch(API + '/assistant/chat', { method: 'POST', body: chatBody })
      .then(function (r) { return r.json().then(function (d) { return { d: d, status: r.status }; }); }).then(function (x) {
        var d = x.d;
        setBusy(false);
        if (d && (d.answer || d.guide)) {
          if (shotKept) clearShot();
          var replies = [];
          if (d.answer) replies.push({ role: 'assistant', content: d.answer, logId: d.logId, screens: d.screens, report: d.report, route: d.route, asked: q });
          if (d.guide) replies.push({ role: 'assistant', guide: d.guide });
          // 대화로 접수됐으면(d.filed) 앞서 띄운 접수 버튼 중 가장 최근 것을 '접수 완료' 로 — 버튼까지 눌러 두 번 들어가지 않게
          var prior = base;
          if (d.filed && d.filed.id) {
            var k = -1;
            for (var j = base.length - 1; j >= 0; j--) { if (base[j].report && base[j].reportState !== 'done') { k = j; break; } }
            if (k >= 0) prior = base.map(function (x, j) { return j === k ? Object.assign({}, x, { reportState: 'done', reportId: d.filed.id }) : x; });
          }
          setMsgs(prior.concat(replies));
        }
        else setMsgs(base.concat([{ role: 'assistant', content: (d && d.error) || '답변을 가져오지 못했습니다.' }]));
      }).catch(function () {
        setBusy(false);
        setMsgs(base.concat([{ role: 'assistant', content: '네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.' }]));
      });
  };
  // 한글 IME 조합 중 Enter 는 글자 확정용 — 여기서 보내면 확정된 마지막 글자가 입력칸에 남는다.
  var isComposing = function (e) { return !!(e.nativeEvent && e.nativeEvent.isComposing) || e.keyCode === 229; };
  sendRef.current = send;
  var onKey = function (e) { if (e.key === 'Enter' && !e.shiftKey && !isComposing(e)) { e.preventDefault(); send(); } };

  // 오류 신고 접수 — 에이전트가 [신고:...] 를 넣은 답변에만 버튼이 붙는다. 접수는 사용자가 누를 때만 일어난다.
  //   화면 우하단 🐛 버튼과 **같은 엔드포인트**(POST /bug-reports)를 쓴다 — 신고함·관리자 알림 경로가 하나여야 한다.
  //   제목은 서버가 정제해 내려준 값, 설명은 사용자가 실제로 물어본 문장이다(모델 추측을 설명에 넣지 않는다).
  var fileReport = function (idx) {
    var m = msgs[idx];
    if (!m || !m.report || m.reportState === 'sending' || m.reportState === 'done') return;
    var mark = function (state, id) {
      setMsgs(function (prev) {
        return prev.map(function (x, i) { return i === idx ? Object.assign({}, x, { reportState: state, reportId: id }) : x; });
      });
    };
    mark('sending');
    authFetch(API + '/bug-reports', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category: m.report.category,
        title: m.report.title,
        description: m.asked || '',
        page: 'assistant-agent'
      })
    }).then(function (r) { return r.json(); }).then(function (d) {
      if (d && d.success) mark('done', d.id);
      else mark('fail');
    }).catch(function () { mark('fail'); });
  };

  // 화면 바로가기 — 서버가 권한 검증한 screens 만 온다. 같은 화면이면 이동 없이 닫기만.
  var goScreen = function (s) {
    if (s && s.path && s.path !== location.pathname) navigate(s.path);
    setOpen(false);
  };

  var giveFeedback = function (idx, helpful) {
    var m = msgs[idx];
    if (!m || !m.logId || m.feedback != null) return;
    setMsgs(function (prev) { var c = prev.slice(); c[idx] = Object.assign({}, c[idx], { feedback: helpful ? 1 : 0 }); return c; });
    authFetch(API + '/assistant/feedback', { method: 'POST', body: JSON.stringify({ logId: m.logId, helpful: helpful }) }).catch(function () {});
  };

  // FAQ 칩 클릭 → 스크린샷 가이드(있으면) 표시, 없으면 LLM 텍스트로 폴백
  var showGuide = function (g) {
    authFetch(API + '/assistant/guides/' + g.id + '/images').then(function (r) { return r.json(); })
      .then(function (d) {
        var imgs = (d && d.images) || [];
        if (!imgs.length) { send(g.question); return; }
        setMsgs(function (prev) {
          return prev.concat([
            { role: 'user', content: g.question },
            { role: 'assistant', guide: { guideId: g.id, title: g.title, question: g.question, images: imgs } }
          ]);
        });
      }).catch(function () { send(g.question); });
  };

  // ── 상담 첨부(이미지) 헬퍼 — 버그신고와 동일 UX ──
  var addSFiles = function (list) {
    if (!list || !list.length) return;
    setSfiles(function (prev) {
      var room = MAX_ATT - prev.length;
      if (room <= 0) return prev;
      var allowed = [];
      for (var i = 0; i < list.length && allowed.length < room; i++) {
        if (String(list[i].type).indexOf('image/') === 0) allowed.push(list[i]);
      }
      if (!allowed.length) return prev;
      var previews = allowed.map(function (f) { return URL.createObjectURL(f); });
      setSpreviews(function (pp) { return pp.concat(previews); });
      return prev.concat(allowed);
    });
  };
  var handleSFileSelect = function (e) {
    if (e.target.files && e.target.files.length) { addSFiles(e.target.files); e.target.value = ''; }
  };
  var removeSFile = function (idx) {
    setSfiles(function (prev) { var n = prev.slice(); n.splice(idx, 1); return n; });
    setSpreviews(function (prev) { var n = prev.slice(); try { URL.revokeObjectURL(n[idx]); } catch (_e) {} n.splice(idx, 1); return n; });
  };
  var clearSFiles = function () {
    setSpreviews(function (prev) { prev.forEach(function (u) { try { URL.revokeObjectURL(u); } catch (_e) {} }); return []; });
    setSfiles([]);
  };
  var handleSPaste = function (e) {
    var items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    var imgs = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') >= 0) { var f = items[i].getAsFile(); if (f) imgs.push(f); }
    }
    if (imgs.length) addSFiles(imgs);
  };
  var loadSupportMessages = function (tid) {
    authFetch(API + '/support/threads/' + tid + '/messages')
      .then(function (r) { return r.json(); })
      .then(function (d) { if (d && d.messages) setSmsgs(d.messages); })
      .catch(function () {});
  };
  // 과거 상담 내역 목록 — 재접속해도 DB(support_threads)에서 복원.
  var loadSupportThreads = function () {
    authFetch(API + '/support/threads')
      .then(function (r) { return r.json(); })
      .then(function (d) { if (d && d.threads) setSthreads(d.threads); })
      .catch(function () {});
  };
  var openSupportThread = function (t) {
    setThreadId(t.id); setSmsgs([]); setSview('thread'); loadSupportMessages(t.id);
  };
  var backToThreadList = function () {
    setThreadId(null); setSmsgs([]); setSview('list'); loadSupportThreads();
  };
  var newSupportThread = function () {
    setThreadId(null); setSmsgs([]); setSview('thread');
  };

  var sendSupport = function () {
    var body = String(sinput || '').trim();
    var filesToSend = sfiles.slice();
    if ((!body && filesToSend.length === 0) || ssending) return;
    var sendBody = body || '(이미지 첨부)';
    setSinput(''); clearSFiles(); setSsending(true);

    var afterMsg = function (tid, messageId) {
      if (filesToSend.length && tid && messageId) {
        var fd = new FormData();
        fd.append('messageId', String(messageId));
        filesToSend.forEach(function (f) { fd.append('files', f); });
        authFetch(API + '/support/threads/' + tid + '/attachments', { method: 'POST', body: fd })
          .then(function (r) { return r.json(); })
          .then(function () { setSsending(false); loadSupportMessages(tid); })
          .catch(function () { setSsending(false); loadSupportMessages(tid); });
      } else {
        setSsending(false);
        if (tid) loadSupportMessages(tid);
      }
    };

    if (!threadId) {
      authFetch(API + '/support/threads', {
        method: 'POST',
        body: JSON.stringify({ firstMessage: sendBody, source: 'assistant', page: currentPage })
      }).then(function (r) { return r.json(); }).then(function (d) {
        if (d && d.threadId) { setThreadId(d.threadId); afterMsg(d.threadId, d.messageId); }
        else { setSsending(false); setSinput(body); }
      }).catch(function () { setSsending(false); setSinput(body); });
    } else {
      authFetch(API + '/support/threads/' + threadId + '/messages', {
        method: 'POST', body: JSON.stringify({ body: sendBody })
      }).then(function (r) { return r.json(); }).then(function (d) {
        afterMsg(threadId, d && d.messageId);
      }).catch(function () { setSsending(false); setSinput(body); });
    }
  };
  var onSupportKey = function (e) { if (e.key === 'Enter' && !e.shiftKey && !isComposing(e)) { e.preventDefault(); sendSupport(); } };

  var resetAi = function () { setMsgs([{ role: 'assistant', content: WELCOME }]); setInput(''); setCtxCar(null); };

  if (!user || !available) return null;

  return (
    <div className={'aw-root' + (L.enabled ? ' aw-lay' : '')} style={L.rootStyle}>
      {open && (
        <div className="aw-panel" role="dialog" aria-label="AI도우미" style={L.panelStyle}>
          {L.enabled && <div className={'aw-grip aw-grip-' + L.grip} title="크기 조절" onPointerDown={L.onGrip} />}
          <div className="aw-header" onPointerDown={L.onHead}>
            <div className="aw-header-title">
              <span className="aw-avatar" aria-hidden="true">🤖</span>
              <span className="aw-htext">
                <span className="aw-hname">{mode === 'support' ? (sview === 'list' ? '상담 내역' : '관리자 상담') : 'AI도우미'}</span>
                {mode !== 'support' && <span className="aw-hsub">{ctxCar ? '차량 ' + ctxCar.no + ' 기준 대화 중' : '무엇이든 편하게 물어보세요 😊'}</span>}
              </span>
            </div>
            <div className="aw-header-actions">
              {L.enabled && <button className="aw-icon-btn" title="위치·크기 초기화" onClick={L.reset}>⇲</button>}
              {mode === 'support'
                ? <React.Fragment>
                    {sview === 'thread' && <button className="aw-icon-btn" title="상담 목록" onClick={backToThreadList}>← 목록</button>}
                    <button className="aw-icon-btn" title="AI도우미로" onClick={function () { switchMode('ai'); }}>AI</button>
                  </React.Fragment>
                : <React.Fragment>
                    <button className="aw-icon-btn" title="대화 초기화" onClick={resetAi}>↻</button>
                  </React.Fragment>}
              <button className="aw-icon-btn" title="닫기" onClick={function () { setOpen(false); }}>✕</button>
            </div>
          </div>

          {mode !== 'support' ? (
            <React.Fragment>
              <div className="aw-body" ref={scrollRef}>
                {(msgs.length <= 1 && !busy) ? (
                  <div className="aw-intro">
                    <div className="aw-intro-title">궁금증을 해결해 드릴게요</div>
                    <div className="aw-intro-sub">{agentEnabled ? '사용법 안내는 물론, 내 차량·서류·말소 진행·인보이스 현황도 조회해 드려요. 내 회사 데이터만 조회합니다.' : '차량 등록·말소대행·인보이스·세무 등 사용법이 궁금하시면 편하게 물어보세요.'}</div>
                    <div className="aw-notice">
                      <div className="aw-notice-title">아래 내용을 꼭 확인해 주세요</div>
                      <ul>
                        <li>AI 안내는 참고용이에요. 실제 처리는 화면에서 확인해 주세요.</li>
                        <li>서비스 개선을 위해 대화 내용이 저장될 수 있어요.</li>
                        <li>개인정보·민감정보가 담긴 질문은 피해 주세요.</li>
                      </ul>
                    </div>
                    <div className="aw-suggest">
                      {guideList.length > 0
                        ? guideList.map(function (g) { return <button key={'g' + g.id} className="aw-chip" onClick={function () { showGuide(g); }}>{g.question}</button>; })
                        : suggestionsFor(user && user.role).map(function (s, i) { return <button key={i} className="aw-chip" onClick={function () { send(s); }}>{s}</button>; })}
                    </div>
                  </div>
                ) : msgs.map(function (m, i) {
                  if (m.guide) {
                    return (
                      <div key={i} className="aw-msg aw-bot">
                        <div className="aw-bubble aw-note">아래 화면을 순서대로 따라 해 보세요.{m.guide.title ? ' — ' + m.guide.title : ''}</div>
                        <div className="aw-guide">
                          {m.guide.images.map(function (im, gi) {
                            return (
                              <div key={im.id} className="aw-guide-step">
                                {(im.caption || m.guide.images.length > 1) && <div className="aw-guide-cap">{gi + 1}{im.caption ? '. ' + im.caption : ''}</div>}
                                {guideImgUrls[im.id]
                                  ? <img src={guideImgUrls[im.id]} alt={'step' + gi} className="aw-guide-img" onClick={function () { setLightbox(guideImgUrls[im.id]); }} />
                                  : <div className="aw-guide-img aw-att-loading">🖼️</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={i} className={'aw-msg ' + (m.role === 'user' ? 'aw-user' : 'aw-bot')}>
                      <div className="aw-bubble">{m.role === 'user' ? m.content : renderMarkdown(m.content)}{m.shot ? <div className="aw-shot-tag">📎 스크린샷</div> : null}</div>
                      {m.role === 'assistant' && m.route === 'agent' ? <div className="aw-route-tag">📊 내 회사 데이터를 조회해 답했어요</div> : null}
                      {m.role === 'assistant' && m.screens && m.screens.length ? <div className="aw-nav">{m.screens.map(function (s) { return (
                        <button key={s.id} type="button" className="aw-nav-btn" onClick={function () { goScreen(s); }}>{s.label} 열기 →</button>
                      ); })}</div> : null}
                      {m.role === 'assistant' && m.report ? (
                        <div className="aw-report">
                          {m.reportState === 'done' ? (
                            <span className="aw-report-done">✅ 접수 완료{m.reportId ? ' (#' + m.reportId + ')' : ''} — 답변은 📋 내 신고 목록에서 확인하세요.</span>
                          ) : (
                            <span>
                              <button type="button" className="aw-report-btn" disabled={m.reportState === 'sending'}
                                onClick={function () { fileReport(i); }}>
                                {m.reportState === 'sending' ? '접수 중…' : '🐛 ' + REPORT_CAT_KR[m.report.category] + ' 신고하기'}
                              </button>
                              <span className="aw-report-title">{m.report.title}</span>
                              {m.reportState === 'fail' && <span className="aw-report-fail">접수에 실패했어요. 다시 눌러 주세요.</span>}
                            </span>
                          )}
                        </div>
                      ) : null}
                      {m.role === 'assistant' && m.logId ? (
                        <div className="aw-fb">
                          {m.feedback == null ? (
                            <span>
                              <button className="aw-fb-btn" title="도움됨" onClick={function () { giveFeedback(i, true); }}>👍</button>
                              <button className="aw-fb-btn" title="아쉬움" onClick={function () { giveFeedback(i, false); }}>👎</button>
                            </span>
                          ) : (<span className="aw-fb-done">{m.feedback ? '👍 감사합니다' : '👎 참고할게요'}</span>)}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {busy && (<div className="aw-msg aw-bot"><div className="aw-bubble aw-typing"><React.Fragment><span /><span /><span /></React.Fragment></div></div>)}
              </div>
              <div className="aw-escalate">
                <span>원하는 답을 못 찾으셨나요?</span>
                <button className="aw-escalate-btn" onClick={function () { openBugReport('question'); }}>📝 문의 남기기</button>
              </div>
              <div className="aw-input aw-input-ai" onPaste={onShotPaste}>
                {screenshotEnabled && (shot || shotNote) && (
                  <div className="aw-shot-area">
                    {shot && <div className="aw-att-prev"><img src={shotPrev} alt="스크린샷 미리보기" /><button title="첨부 제거" onClick={clearShot}>✕</button></div>}
                    <span className={shotNote ? 'aw-shot-err' : 'aw-shot-info'}>{shotNote || '보낸 화면은 답변에만 쓰이고 저장되지 않습니다'}</span>
                  </div>
                )}
                <textarea value={input} placeholder={agentEnabled ? '예: 서류 안 올라온 차 알려 줘 · 말소대행 신청은 어디서 하나요?' : '궁금한 사용법을 구체적으로 입력해 주세요. (예: 말소대행 신청은 어디서 하나요?)'}
                  maxLength={600}
                  onChange={function (e) { setInput(e.target.value); }} onKeyDown={onKey} rows={1} />
                <div className="aw-input-bar">
                  <span className="aw-count">{input.length}/600</span>
                  {screenshotEnabled && !ctxCar && (
                    <label className="aw-att-add" title="스크린샷 첨부">📎
                      <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={onShotSelect} />
                    </label>
                  )}
                  <button className="aw-send" disabled={busy || !input.trim()} onClick={function () { send(); }}>
                    <span className="aw-send-ico" aria-hidden="true">✈</span> 보내기
                  </button>
                </div>
              </div>
              <div className="aw-foot">AI 안내는 참고용입니다. 정확한 처리는 화면에서 확인하세요.</div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {sview === 'list' ? (
              <React.Fragment>
                <div className="aw-body">
                  <div className="aw-msg aw-bot"><div className="aw-bubble aw-note">이전 상담 내역입니다. 이어서 문의하려면 항목을 누르고, 새 문의는 아래 버튼을 누르세요.</div></div>
                  {sthreads.length === 0 && <div className="aw-empty">아직 상담 내역이 없습니다.</div>}
                  {sthreads.map(function (t) {
                    var st = t.status === 'answered' ? { t: '답변됨', c: 'ans' } : (t.status === 'closed' ? { t: '종료', c: 'cls' } : { t: '대기중', c: 'opn' });
                    return (
                      <button key={t.id} className="aw-thread-item" onClick={function () { openSupportThread(t); }}>
                        <div className="aw-thread-subj">{t.subject || '문의'}</div>
                        <div className="aw-thread-meta">
                          <span className={'aw-thread-badge aw-tb-' + st.c}>{st.t}</span>
                          <span className="aw-thread-date">{String(t.updated_at || t.created_at || '').slice(0, 16)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="aw-input">
                  <button className="aw-send aw-newthread" onClick={newSupportThread}>+ 새 문의하기</button>
                </div>
              </React.Fragment>
              ) : (
              <React.Fragment>
              <div className="aw-body" ref={scrollRef}>
                <div className="aw-msg aw-bot"><div className="aw-bubble aw-note">{SUPPORT_INTRO}</div></div>
                {smsgs.map(function (m, i) {
                  var mine = m.sender_kind === 'user';
                  var atts = m.attachments || [];
                  return (
                    <div key={m.id || i} className={'aw-msg ' + (mine ? 'aw-user' : 'aw-bot')}>
                      {!mine && <div className="aw-sender">🧑‍💼 관리자</div>}
                      {m.body && m.body !== '(이미지 첨부)' && <div className="aw-bubble">{m.body}</div>}
                      {atts.length > 0 && (
                        <div className="aw-att-row">
                          {atts.map(function (a) {
                            return attUrls[a.id]
                              ? <img key={a.id} src={attUrls[a.id]} alt={a.file_name || 'att'} className="aw-att-thumb" onClick={function () { setLightbox(attUrls[a.id]); }} />
                              : <div key={a.id} className="aw-att-thumb aw-att-loading">🖼️</div>;
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {ssending && (<div className="aw-msg aw-user"><div className="aw-bubble aw-typing"><span /><span /><span /></div></div>)}
              </div>
              {spreviews.length > 0 && (
                <div className="aw-att-previews">
                  {spreviews.map(function (p, idx) {
                    return (
                      <div key={idx} className="aw-att-prev">
                        <img src={p} alt={'p' + idx} />
                        <button onClick={function () { removeSFile(idx); }}>✕</button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="aw-input" onPaste={handleSPaste}>
                <label className="aw-att-add" title="이미지 첨부 (최대 5장)">📎
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: 'none' }} onChange={handleSFileSelect} />
                </label>
                <textarea value={sinput} placeholder="관리자에게 남길 문의를 입력하세요…"
                  onChange={function (e) { setSinput(e.target.value); }} onKeyDown={onSupportKey} rows={1} />
                <button className="aw-send" disabled={ssending || (!sinput.trim() && sfiles.length === 0)} onClick={sendSupport}>
                  <span className="aw-send-ico" aria-hidden="true">✈</span> 보내기
                </button>
              </div>
              <div className="aw-foot">관리자 부재 시에도 문의는 저장되며, 답변 시 알림으로 안내드립니다.</div>
              </React.Fragment>
              )}
            </React.Fragment>
          )}
        </div>
      )}

      {lightbox && (
        <div className="aw-lightbox" onClick={function () { setLightbox(''); }}>
          <img src={lightbox} alt="zoom" />
        </div>
      )}

      <button className={'aw-fab' + (open ? ' aw-fab-open' : '')} onPointerDown={L.onFab} onClick={L.click(function () { setOpen(!open); })} title="AI도우미">
        {open ? '✕' : (<React.Fragment><span className="aw-fab-bot" aria-hidden="true">🤖</span> AI도우미</React.Fragment>)}
      </button>
    </div>
  );
}

export default AssistantWidget;
